import type { CocoRle, PixelBox } from "@faro-lotv/service-wires";

/** A part of a mask, rasterized into a single-channel bitmap */
export type MaskCrop = {
  /** The coverage of each texel, 0 or COVERED, row by row, top row first */
  data: Uint8Array;

  /** The width of the bitmap in texels */
  width: number;

  /** The height of the bitmap in texels */
  height: number;
};

/** The compressed counts string stores 5 bits of a value per character... */
const BITS_PER_CHAR = 5;
const VALUE_BITS = 0x1f;
/** ...plus a flag that the value continues in the next character... */
const CONTINUATION_BIT = 0x20;
/** ...and, in a value's last character, the sign of the value */
const SIGN_BIT = 0x10;
/** The characters start at '0', so they are all printable */
const CHAR_OFFSET = 48;

/** The coverage of a texel inside the mask */
export const COVERED = 255;

/**
 * Decode pycocotools' compressed counts string into run lengths. Port of `rleFrString` in
 * pycocotools' maskApi.c: each value is stored in 6-bit groups offset by 48 ('0'), with a continuation bit,
 * and from the third value on as the difference to the value two positions earlier.
 *
 * @param counts The compressed counts string.
 * @returns The run lengths, alternating zeros and ones, starting with zeros.
 */
export function decodeRleCounts(counts: string): number[] {
  const runs: number[] = [];
  let p = 0;
  while (p < counts.length) {
    let x = 0;
    let k = 0;
    let more = true;
    while (more) {
      const c = counts.charCodeAt(p) - CHAR_OFFSET;
      x |= (c & VALUE_BITS) << (BITS_PER_CHAR * k);
      more = (c & CONTINUATION_BIT) !== 0;
      p++;
      k++;
      if (!more && (c & SIGN_BIT) !== 0) {
        x |= -1 << (BITS_PER_CHAR * k);
      }
    }
    if (runs.length > 2) {
      x += runs[runs.length - 2];
    }
    runs.push(x);
  }
  return runs;
}

/**
 * Rasterize the part of a mask inside a box, without allocating the whole mask. The mask is column-major, so each
 * run of ones covers a vertical stretch of one or more columns.
 *
 * @param rle The mask.
 * @param box The part of the mask's frame to rasterize, in pixels.
 * @param maxSize The maximum width and height of the result. Larger boxes are downsampled.
 * @returns The coverage of the box.
 */
export function rasterizeRleCrop(
  rle: CocoRle,
  box: PixelBox,
  maxSize: number,
): MaskCrop {
  const [frameHeight, frameWidth] = rle.size;
  const x0 = Math.max(0, Math.floor(box.x));
  const y0 = Math.max(0, Math.floor(box.y));
  const x1 = Math.min(frameWidth, Math.ceil(box.x + box.width));
  const y1 = Math.min(frameHeight, Math.ceil(box.y + box.height));

  const scale = Math.max(1, Math.ceil(Math.max(x1 - x0, y1 - y0) / maxSize));
  const width = Math.max(1, Math.ceil((x1 - x0) / scale));
  const height = Math.max(1, Math.ceil((y1 - y0) / scale));
  const data = new Uint8Array(width * height);

  const runs = decodeRleCounts(rle.counts);
  let start = 0;
  for (let i = 0; i < runs.length; i++) {
    const end = start + runs[i];
    // Odd runs are ones
    if (i % 2 === 1 && end > start) {
      const firstColumn = Math.max(x0, Math.floor(start / frameHeight));
      const lastColumn = Math.min(x1 - 1, Math.floor((end - 1) / frameHeight));
      for (let column = firstColumn; column <= lastColumn; column++) {
        if ((column - x0) % scale !== 0) continue;

        const columnStart = column * frameHeight;
        const top = Math.max(y0, start - columnStart);
        const bottom = Math.min(y1, end - columnStart);
        if (top >= bottom) continue;

        const texelX = (column - x0) / scale;
        const firstRow = Math.floor((top - y0) / scale);
        const lastRow = Math.floor((bottom - 1 - y0) / scale);
        for (let row = firstRow; row <= lastRow; row++) {
          data[row * width + texelX] = COVERED;
        }
      }
    }
    start = end;
  }

  return { data, width, height };
}
